// Import required libraries
import React, { useState, useEffect } from "react";
import { useMsal, useIsAuthenticated } from "@azure/msal-react";
import { InteractionStatus } from "@azure/msal-browser";
import { loginRequest } from "../msalConfig";
import PropTypes from "prop-types";
import { connect } from "react-redux";
import { UserData } from "../js/utils/MockDataTemplateMapping";
import sidenavData from "../assets/data/sidenavData.json";

// Import styles
import "./App.scss";

// Import routes
import Routes from "./routes/Routes";

// Import action creators
import {
  updateUserDetails,
  updateUserInfo,
  updateAlert,
} from "./redux/actions";

// Import utils
import { config } from "../js/config/config";

//Import custom components
import Loader from "./components/Loader/Loader";
import Login from "./screens/Login/Login";

function App(props) {
  let { user_detail, updateUserDetails, updateUserInfo, updateAlert } = props;
  let api = config.api.api_url;
  const { instance, inProgress, accounts } = useMsal();
  const isAuthenticated = useIsAuthenticated();
  const isMsalReady = inProgress === InteractionStatus.None;

  console.log(process.env.REACT_APP_ENVIRONMENT);

  const [isUserDataLoaded, setIsUserDataLoaded] = useState(false);
  const [isUserHaveAppAccount, setUserHaveAppAccount] = useState(true);
  const [authError, setAuthError] = useState(null);

  // --- Sets basic display info after MSAL login
  const setBasicUserInfo = (account) => {
    updateUserInfo("displayName", account.name);
    updateUserInfo("id", account.localAccountId);
    updateUserInfo("mail", account.username);
    updateAlert({
      open: true,
      message: `Welcome ${account.name}`,
      severity: "success",
      closeFlag: true,
    });
  };

  // --- Fetch app-specific user data from backend
  const fetchAndStoreUserData = async (accessToken, graphToken, email) => {
    try {
      const res = await fetch(`${api}/userdata`, {
        method: "POST",
        mode: "cors",
        credentials: "include",
        headers: {
          Authorization: `Bearer ${accessToken}`,
          "Content-Type": "application/json",
          graphtoken: `Bearer ${graphToken}`,
        },
      });

      const data = await res.json();

      if (!res.ok) {
        if (data?.statusCode === "harbor-401") {
          updateAlert({
            open: true,
            message: `User Unauthorized. Please sign in.`,
            severity: "warning",
            closeFlag: true,
          });
          setUserHaveAppAccount(false);
          return false; // indicate failure
        }
        throw new Error("Unexpected error occured. Please try again later");
      }

      const ROLE = data?.data?.user_collection?.role || "";
      const Approval_Screens = data?.data?.allowed_approval || [];
      const TICKET_PORTAL =
        data?.data?.common_details?.ticket_portal_details || {};

      // Save user data
      updateUserDetails("user_info", data.data);
      updateUserInfo("role", ROLE);
      updateUserInfo("ticketPortal", TICKET_PORTAL);
      updateUserInfo("approval_screens", Approval_Screens);
      setIsUserDataLoaded(true);
      return true; // indicate success
    } catch (err) {
      setAuthError(`Unexpected error occured. Please try again later`);
      updateAlert({
        open: true,
        message: `Unexpected error occured. Please try again later`,
        severity: "warning",
        closeFlag: true,
      });
      return false;
    }
  };

  // --- Handle authentication + data fetch
  const handleAuth = async () => {
    if (!accounts.length) return;
    const account = accounts[0]; //Gets account from MSAL
    setBasicUserInfo(account);

    try {
      // MSAL Token for custom API
      const tokenResponse = await instance.acquireTokenSilent({
        ...loginRequest,
        account: account,
      });

      // Token for Microsoft Graph
      const graphTokenResponse = await instance.acquireTokenSilent({
        scopes: ["User.Read"], // Or whatever Graph scopes you added
        account: account,
      });

      // Gets Access Token
      const accessToken = tokenResponse.accessToken;
      const graphToken = graphTokenResponse.accessToken;
      const mail = tokenResponse.idTokenClaims.email;

      updateUserInfo("token", accessToken);
      const ok = await fetchAndStoreUserData(accessToken, graphToken, mail);
      if (!ok) throw new Error("Failed to fetch user data");
    } catch (err) {
      if (
        err.errorCode === "interaction_required" ||
        err.errorCode === "login_required" ||
        err.errorCode === "token_renewal_error"
      ) {
        updateAlert({
          open: true,
          message: `Silent token failed, falling back to redirect login - ${err}`,
          severity: "error",
          closeFlag: true,
        });
        setAuthError(
          `Silent token failed, falling back to redirect login - ${err}` ||
            "Authentication failed",
        );
        localStorage.clear();
        sessionStorage.clear();
        instance.loginRedirect(loginRequest);
        // await instance.acquireTokenRedirect({
        //   ...loginRequest,
        //   account: accounts[0],
        // });
      } else {
        setAuthError(err.message || "Authentication failed");
        updateAlert({
          open: true,
          message: `Login failed: ${err.message || err}`,
          severity: "error",
          closeFlag: true,
        });
      }
    }
  };

  // --- Trigger auth flow after MSAL is ready
  useEffect(() => {
    if (isMsalReady && isAuthenticated && accounts.length > 0) {
      handleAuth();
    }
  }, [isMsalReady, isAuthenticated, accounts, instance]);

  // Helper to fully reset session and re-login
  const handleFullReLogin = () => {
    localStorage.clear();
    sessionStorage.clear();
    instance.loginRedirect(loginRequest);
  };

  // Auto-redirect on refresh for no-account or error screens
  useEffect(() => {
    const refreshFlag = sessionStorage.getItem("forceRelogin");

    // If the flag is set, immediately trigger login
    if (refreshFlag === "true") {
      sessionStorage.removeItem("forceRelogin");
      handleFullReLogin();
    }
  }, []);

  // UnComment to test in local start -----
  // useEffect(() => {
  //   const ROLE = UserData?.data?.user_collection?.role || "";
  //   const Approval_Screens = UserData?.data?.allowed_approval || [];
  //   const MAIL = UserData?.data?.mail || "";
  //   const TICKET_PORTAL = UserData?.data?.common_data?.ticket_portal || {};

  //   updateUserInfo("role", ROLE);
  //   updateUserInfo("approval_screens", Approval_Screens);
  //   updateUserInfo("mail", MAIL);
  //   updateUserInfo("ticketPortal", TICKET_PORTAL);

  //   const USER_INFO = {
  //     allowed_sidenav: sidenavData || [],
  //   };

  //   updateUserDetails("user_info", USER_INFO);
  // }, []);
  // UnComment to test in local end --------

  // ---------------- Render Logic ----------------

  // 1) Shows loader while MSAL is still starting
  if (!isMsalReady) {
    return <Loader fullPage={true} message="Initializing authentication..." />;
  }

  // 2) If MSAL ready but user not authenticated → show login
  if (!isAuthenticated) {
    return (
      <Login initialeSignIn={() => instance.loginRedirect(loginRequest)} />
    );
  }

  // 3) If auth error occurred → show error screen
  if (authError) {
    // Set flag so refresh triggers auto-login
    sessionStorage.setItem("forceRelogin", "true");

    return (
      <div
        style={{
          display: "flex",
          flexDirection: "column",
          justifyContent: "center",
          alignItems: "center",
          height: "100vh",
          backgroundColor: "#f8f9fa",
        }}
      >
        <span>
          <i
            className="material-icons-outlined"
            style={{ color: "red", fontSize: "64px" }}
          >
            error
          </i>
        </span>
        <p style={{ marginTop: "15px", fontSize: "16px", color: "#333" }}>
          {authError}
        </p>
        <button
          style={{
            marginTop: "20px",
            padding: "10px 20px",
            fontSize: "14px",
            backgroundColor: "#dc3545",
            color: "#fff",
            border: "none",
            borderRadius: "4px",
            cursor: "pointer",
          }}
          onClick={handleFullReLogin}
        >
          Try Again
        </button>
      </div>
    );
  }

  // 4) If authenticated but still fetching user data → show loader
  if (isAuthenticated && !isUserDataLoaded && isUserHaveAppAccount) {
    return <Loader fullPage={true} message="Loading user data..." />;
  }

  // 5) If user has no account → show "no account" page
  if (!isUserHaveAppAccount) {
    // Set flag so refresh triggers auto-login
    sessionStorage.setItem("forceRelogin", "true");

    return (
      <div
        style={{
          display: "flex",
          flexDirection: "column",
          justifyContent: "center",
          alignItems: "center",
          height: "100vh",
          backgroundColor: "#f8f9fa",
        }}
      >
        <i
          className="material-icons-outlined"
          style={{ color: "blue", fontSize: "64px" }}
        >
          no_accounts
        </i>
        <p style={{ marginTop: "15px", fontSize: "16px", color: "#333" }}>
          You don't have access to this app. Please reach out to the admin.
        </p>
        <button
          style={{
            marginTop: "20px",
            padding: "10px 20px",
            fontSize: "14px",
            backgroundColor: "#007bff",
            color: "#fff",
            border: "none",
            borderRadius: "4px",
            cursor: "pointer",
          }}
          onClick={handleFullReLogin}
        >
          Back to Sign In
        </button>
      </div>
    );
  }

  // 6) Everything good → render routes
  return <Routes />;
}

App.propTypes = {
  match: PropTypes.object,
};

const mapStateToProps = (state) => ({
  user_detail: state.user_detail,
});

const mapDispatchToProps = { updateUserDetails, updateUserInfo, updateAlert };

export default connect(mapStateToProps, mapDispatchToProps)(App);
